import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { useSelector } from 'react-redux'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '../Button'
import { isRtlSelector } from 'redux/selectors/general'

/**
 * Universal single-row horizontal scroller.
 *
 * - Lays its children out in one non-wrapping row that scrolls horizontally.
 * - Hides the scrollbar entirely (all browsers).
 * - RTL-aware: scroll direction and the edge fades flip automatically.
 * - Fades (opacity mask) the edge(s) where content is clipped, so a partial
 *   chip/word dissolves instead of being hard-cut. The fade only shows on a side
 *   that actually has more content to scroll to.
 *
 * Drop any children in — it does not care what they are.
 */

const ScrollArea = styled.div`
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -ms-overflow-style: none; /* IE / old Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome / Safari */
  }
`

const Row = styled.div<{ gap: string; centered: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  width: max-content;
  min-width: ${(props) => (props.centered ? '100%' : '0')};
  justify-content: ${(props) => (props.centered ? 'center' : 'flex-start')};
  gap: ${(props) => props.gap};
`

export interface IHorizontalScrollerProps {
  children: React.ReactNode
  /** Optional overflow controls; labels are already localized by the caller. */
  navigation?: { previousLabel: string; nextLabel: string }
  /** Center short rows without making overflowing items unreachable. */
  centered?: boolean
  /** Gap between children. Default 14px. */
  gap?: string
  /** Width of the edge fade in px. Default 24. */
  fadeSize?: number
  /** Optional access for keeping a selected item visible without scrolling the page. */
  scrollRef?: React.RefObject<HTMLDivElement>
  className?: string
  style?: React.CSSProperties
}

export const HorizontalScroller: React.FC<IHorizontalScrollerProps> = ({
  children,
  gap = '14px',
  fadeSize = 24,
  navigation,
  centered = false,
  scrollRef,
  className,
  style,
}) => {
  const isRtl = useSelector(isRtlSelector)
  const localRef = useRef<HTMLDivElement | null>(null)
  const ref = scrollRef || localRef
  const rowRef = useRef<HTMLDivElement>(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(true)

  const update = useCallback(() => {
    const el = ref.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    // Math.abs normalizes the sign difference in RTL scrollLeft across browsers.
    const pos = Math.abs(el.scrollLeft)
    const noOverflow = max <= 1
    setAtStart(noOverflow || pos <= 1)
    setAtEnd(noOverflow || pos >= max - 1)
  }, [ref])

  // Map logical start/end to physical left/right edges (flips in RTL).
  const showLeftFade = isRtl ? !atEnd : !atStart
  const showRightFade = isRtl ? !atStart : !atEnd

  const leftStop = showLeftFade ? `rgba(0,0,0,0) 0, rgba(0,0,0,1) ${fadeSize}px` : 'rgba(0,0,0,1) 0'
  const rightStop = showRightFade
    ? `rgba(0,0,0,1) calc(100% - ${fadeSize}px), rgba(0,0,0,0) 100%`
    : 'rgba(0,0,0,1) 100%'
  const maskImage = `linear-gradient(to right, ${leftStop}, ${rightStop})`

  const scrollPage = (direction: 1 | -1) => {
    const el = ref.current
    if (!el) return
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    el.scrollBy({
      left: Math.max(100, el.clientWidth * 0.75) * direction * (isRtl ? -1 : 1),
      behavior: reducedMotion ? 'auto' : 'smooth',
    })
  }

  // Recompute after every render (cheap) so it reacts to children changes,
  // plus on scroll and on resize.
  useEffect(() => {
    update()
    const el = ref.current
    if (!el) return
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    ro?.observe(el)
    if (rowRef.current) ro?.observe(rowRef.current)
    window.addEventListener('resize', update)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', update)
    }
  })

  const content = (
    <ScrollArea
      ref={ref}
      className={className}
      onScroll={update}
      dir={isRtl ? 'rtl' : 'ltr'}
      style={{ maskImage, WebkitMaskImage: maskImage, ...style }}
    >
      <Row ref={rowRef} gap={gap} centered={centered}>
        {children}
      </Row>
    </ScrollArea>
  )

  if (!navigation) return content

  return (
    <div style={{ position: 'relative', display: 'flex', minWidth: 0, width: '100%' }} dir={isRtl ? 'rtl' : 'ltr'}>
      {content}
      {(!atStart || !atEnd) && (
        <>
          <Button
            variant="secondary"
            aria-label={navigation.previousLabel}
            isDisabled={atStart}
            onClick={() => scrollPage(-1)}
            position="absolute"
            top="50%"
            left={isRtl ? undefined : '0'}
            right={isRtl ? '0' : undefined}
            transform="translateY(-50%)"
            width="28px"
            minWidth="28px"
            height="28px"
            p={0}
            borderRadius="50%"
            _hover={{ bg: '#f5f6f8' }}
            _active={{ transform: 'translateY(-50%) scale(0.97)' }}
            _disabled={{ opacity: 0.45, cursor: 'default', transform: 'translateY(-50%)' }}
          >
            {isRtl ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </Button>
          <Button
            variant="secondary"
            aria-label={navigation.nextLabel}
            isDisabled={atEnd}
            onClick={() => scrollPage(1)}
            position="absolute"
            top="50%"
            right={isRtl ? undefined : '0'}
            left={isRtl ? '0' : undefined}
            transform="translateY(-50%)"
            width="28px"
            minWidth="28px"
            height="28px"
            p={0}
            borderRadius="50%"
            _hover={{ bg: '#f5f6f8' }}
            _active={{ transform: 'translateY(-50%) scale(0.97)' }}
            _disabled={{ opacity: 0.45, cursor: 'default', transform: 'translateY(-50%)' }}
          >
            {isRtl ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </Button>
        </>
      )}
    </div>
  )
}
